import React from "react";
import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import { forwardRef, ReactElement } from "react";
import { cn } from "../../../lib";
import {
  CalendarIcon,
  ChevronRightIcon,
  UsersIcon,
  HashtagIcon,
  MapPinIcon,
  UserIcon,
} from "@heroicons/react/20/solid";
import { Link } from "react-router-dom";
import TintedLabel from "../../atoms/TintedLabel/TintedLabel";

const eventItemVariants = cva("relative flex justify-between px-6 py-8", {
  variants: {
    variant: {
      white: "bg-white",
      blue: "bg-lf-blue-slate",
      gray: "bg-lf-gray-dark",
    },
    linkVariant: {
      white: "hover:bg-gray-50",
      blue: "hover:bg-lf-blue-light-2",
      gray: "hover:bg-gray-400",
      none: "",
    },
  },

  defaultVariants: {
    variant: "white",
  },
});

const borderVariants = cva("border-0", {
  variants: {
    borderVariant: {
      none: "",
      red: "border border-red-700",
    },
  },
});

const textColorVariants = cva("", {
  variants: {
    variant: {
      white: "text-lf-blue-light",
      blue: "text-gray-50",
      gray: "text-gray-50",
    },
  },
});

type EventItemVariant = "white" | "blue" | "gray";

interface EventItemProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof eventItemVariants>,
    VariantProps<typeof textColorVariants>,
    VariantProps<typeof borderVariants> {
  id: string;
  date: string;
  time: string;
  datetime: string;
  name: string;
  linkUrl: string;
  location: string;
  directors: string;
  representatives: string;
  planners: string;
  badge: string;
  action: ReactElement<any, any>;
}

const renderBadgeCount = (event: Partial<EventItemProps>) => {
  const { variant } = event;

  return (
    <span
      className={cn(
        textColorVariants({ variant }),
        "pr-10 font-semibold text-lg xl:pr-0",
      )}
    >
      {(event.badge ?? "0") !== "0" && (
        <span className="inline-flex h-6 mr-2 items-center rounded-md bg-blue-50 px-2 py-1 text-sm font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
          <UsersIcon
            className="h-4 w-4 text-lf-blue-light mr-1"
            aria-hidden="true"
          />
          {event.badge}
        </span>
      )}
      {event.name}
    </span>
  );
};

const renderEventData = (event: Partial<EventItemProps>) => {
  const { variant, date, datetime, location, id } = event;

  return (
    <div className="flex-1">
      <dl className="mt-2 flex flex-col md:flex-row">
        <div className="flex items-start space-x-3">
          <dt className="mt-0">
            <span className="sr-only">Date</span>
            <CalendarIcon
              title="Date"
              className={cn(textColorVariants({ variant }), "h-5 w-5")}
              aria-hidden="true"
            />
          </dt>
          <dd>
            <time
              dateTime={datetime}
              className={cn(textColorVariants({ variant }))}
            >
              {date}
            </time>
          </dd>
        </div>
        <div className="mt-2 flex items-start space-x-3 md:ml-3.5 md:mt-0 md:border-l md:border-gray-400 md:border-opacity-50 md:pl-3.5">
          <dt className="mt-0">
            <span className="sr-only">Location</span>
            <MapPinIcon
              title="Location"
              className={cn(textColorVariants({ variant }), "h-5 w-5")}
              aria-hidden="true"
            />
          </dt>
          <dd className={cn(textColorVariants({ variant }))}>
            {location ? (
              location
            ) : (
              <TintedLabel variant="red" label="Missing" className="" />
            )}
          </dd>
        </div>
        <div className="mt-2 flex items-start space-x-3 md:ml-3.5 md:mt-0 md:border-l md:border-gray-400 md:border-opacity-50 md:pl-3.5">
          <dt className="mt-0">
            <span className="sr-only">E Number</span>
            <HashtagIcon
              title="E Number"
              className={cn(textColorVariants({ variant }), "h-5 w-5")}
              aria-hidden="true"
            />
          </dt>
          <dd className={cn(textColorVariants({ variant }))}>{id}</dd>
        </div>
        <div className="mt-2 flex shrink-0 items-start space-x-3 md:ml-3.5 md:mt-0 md:border-l md:border-gray-400 md:border-opacity-50 md:pl-3.5">
          <dt className="mt-0">
            <span className="sr-only">Representative</span>
            <UserIcon
              title="Representative"
              className={cn(textColorVariants({ variant }), "h-5 w-5")}
              aria-hidden="true"
            />
          </dt>
          <dd className={cn(textColorVariants({ variant }))}>
            {event.representatives}
          </dd>
        </div>
        <div className="mt-2 flex shrink-0 items-start space-x-3 md:ml-3.5 md:mt-0 md:border-l md:border-gray-400 md:border-opacity-50 md:pl-3.5">
          <dt className="mt-0">
            <span className="sr-only">Planner</span>
            <UserIcon
              title="Planner"
              className={cn(textColorVariants({ variant }), "h-5 w-5")}
              aria-hidden="true"
            />
          </dt>
          <dd className={cn(textColorVariants({ variant }))}>
            {event.planners}
          </dd>
        </div>
      </dl>
    </div>
  );
};

const renderItemContent = (props: Partial<EventItemProps>) => {
  const {
    variant = "white",
    borderVariant = "none",
    className,
    ...event
  } = props;

  return (
    <div
      className={cn(
        eventItemVariants({
          variant,
          linkVariant: props.linkUrl ? variant : "none",
        }),
        borderVariants({ borderVariant }),
        className,
      )}
    >
      <div className="relative flex-col">
        {renderBadgeCount(props)}
        {renderEventData(props)}
      </div>
      {event.linkUrl && (
        <div className="flex self-center justify-end ml-4">
          <ChevronRightIcon
            aria-hidden="true"
            className="h-5 w-5 flex-none text-gray-400"
          />
        </div>
      )}
    </div>
  );
};

// Header component used for Tax Forms unified table
const EventItem = forwardRef<HTMLDivElement, Partial<EventItemProps>>(
  (props, ref) => {
    const { className, ...restProps } = props;
    const { id, linkUrl } = restProps;
    return (
      <div ref={ref} className={className}>
        {linkUrl && (
          <Link key={`${id}`} to={linkUrl ?? "#"} className="block">
            {renderItemContent(restProps)}
          </Link>
        )}
        {!linkUrl && renderItemContent(restProps)}
      </div>
    );
  },
);

EventItem.displayName = "Event Item";
export default EventItem;
export type { EventItemProps, EventItemVariant };
